import { useMemo } from "react"
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"

/**
 * Default chart color palette — matches shadcn/ui chart tokens
 */
const CHART_COLORS = [
  "hsl(var(--primary, 222.2 47.4% 11.2%))",
  "hsl(var(--chart-1, 220 70% 50%))",
  "hsl(var(--chart-2, 160 60% 45%))",
  "hsl(var(--chart-3, 30 80% 55%))",
  "hsl(var(--chart-4, 280 65% 60%))",
  "hsl(var(--chart-5, 340 75% 55%))",
]

/**
 * Cogo JSON chart schema:
 *
 * {
 *   "type": "chart",
 *   "chartType": "area" | "line" | "bar" | "pie" | "donut",
 *   "className": "h-[300px] w-full",
 *   "data": "#{ui.chartData}",
 *   "options": {
 *     "xAxis": { "key": "month", "hide": false },
 *     "yAxis": { "key": "volume", "hide": false, "tickFormatter": "compact" },
 *     "series": [
 *       { "key": "volume", "color": "#8884d8", "name": "Volume" },
 *       { "key": "revenue", "color": "#82ca9d", "name": "Revenue" }
 *     ],
 *     "grid": { "horizontal": true, "vertical": false, "strokeDasharray": "3 3" },
 *     "tooltip": { "show": true },
 *     "legend": { "show": false, "position": "bottom" },
 *     "gradient": true,
 *     "curved": true,
 *     "stacked": false,
 *     "innerRadius": 60,
 *     "outerRadius": 80
 *   }
 * }
 */

interface ChartOptions {
  xAxis?: { key?: string; hide?: boolean }
  yAxis?: { key?: string; hide?: boolean; tickFormatter?: string }
  series?: Array<{ key: string; color?: string; name?: string }>
  grid?: { horizontal?: boolean; vertical?: boolean; strokeDasharray?: string }
  tooltip?: { show?: boolean }
  legend?: { show?: boolean; position?: string }
  gradient?: boolean
  curved?: boolean
  stacked?: boolean
  innerRadius?: number
  outerRadius?: number
}

function compactNumber(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`
  return String(value)
}

export function ChartRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const rawChartType = element.chartType ?? element.variant ?? "bar"
  const chartType =
    typeof rawChartType === "string" && rawChartType.startsWith("#{")
      ? String(resolveSymbol(rawChartType) ?? "bar")
      : rawChartType

  const opts = (element.options ?? {}) as ChartOptions
  const xKey = opts.xAxis?.key ?? "label"
  const yKeys = opts.series?.map((s) => s.key) ?? [opts.yAxis?.key ?? "value"]
  const gradient = opts.gradient !== false
  const curved = opts.curved !== false
  const stacked = opts.stacked === true
  const showTooltip = opts.tooltip?.show !== false
  const showLegend = opts.legend?.show === true
  const showGrid = opts.grid ?? { horizontal: true, vertical: false }
  const gridDash = opts.grid?.strokeDasharray ?? "3 3"

  const data = useMemo(() => {
    if (element.data == null) return []

    if (typeof element.data === "string") {
      const resolved = resolveSymbol(element.data)
      if (Array.isArray(resolved)) return resolved as Record<string, unknown>[]
      return []
    }

    // Chart.js format: { labels: [], datasets: [{ data: [] }] }
    const dataObj = element.data as Record<string, unknown>
    if (dataObj.labels && Array.isArray(dataObj.labels) && dataObj.datasets) {
      const labels = dataObj.labels as string[]
      const datasets = dataObj.datasets as Array<Record<string, unknown>>
      return labels.map((label, i) => {
        const row: Record<string, unknown> = { [xKey]: label }
        datasets.forEach((ds, di) => {
          const values = ds.data as number[]
          row[yKeys[di] ?? `series${di}`] = values?.[i] ?? 0
        })
        return row
      })
    }

    return []
  }, [element.data, resolveSymbol, xKey, yKeys])

  const seriesDefs = useMemo(() => {
    if (opts.series && opts.series.length > 0) return opts.series
    return yKeys.map((key, i) => ({
      key,
      color: CHART_COLORS[i % CHART_COLORS.length],
      name: key,
    }))
  }, [opts.series, yKeys])

  const yTickFormatter =
    opts.yAxis?.tickFormatter === "compact" ? compactNumber : undefined

  // ─── Area Chart ───────────────────────────────────────────────
  if (chartType === "area") {
    return (
      <div
        className={cn("h-[300px] w-full", element.className)}
        data-testid={`chart-${element.id}`}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            {gradient &&
              seriesDefs.map((s, i) => (
                <defs key={`grad-${i}`}>
                  <linearGradient
                    id={`gradient-${element.id}-${i}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={s.color ?? CHART_COLORS[i]}
                      stopOpacity={0.3}
                    />
                    <stop
                      offset="95%"
                      stopColor={s.color ?? CHART_COLORS[i]}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
              ))}
            <CartesianGrid
              horizontal={showGrid.horizontal !== false}
              vertical={showGrid.vertical === true}
              strokeDasharray={gridDash}
              stroke="hsl(var(--border, 214.3 31.8% 91.4%))"
            />
            {!opts.xAxis?.hide && (
              <XAxis
                dataKey={xKey}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground, 215.4 16.3% 56.9%))" }}
                dy={8}
              />
            )}
            {!opts.yAxis?.hide && (
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground, 215.4 16.3% 56.9%))" }}
                tickFormatter={yTickFormatter}
                width={48}
              />
            )}
            {showTooltip && (
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--background, 0 0% 100%))",
                  border: "1px solid hsl(var(--border, 214.3 31.8% 91.4%))",
                  borderRadius: "8px",
                  fontSize: "13px",
                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                }}
              />
            )}
            {showLegend && <Legend />}
            {seriesDefs.map((s, i) => (
              <Area
                key={s.key}
                type={curved ? "monotone" : "linear"}
                dataKey={s.key}
                name={s.name ?? s.key}
                stroke={s.color ?? CHART_COLORS[i]}
                strokeWidth={2}
                fill={
                  gradient
                    ? `url(#gradient-${element.id}-${i})`
                    : (s.color ?? CHART_COLORS[i])
                }
                fillOpacity={gradient ? 1 : 0.15}
                stackId={stacked ? "stack" : undefined}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, fill: "white" }}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    )
  }

  // ─── Line Chart ───────────────────────────────────────────────
  if (chartType === "line") {
    return (
      <div
        className={cn("h-[300px] w-full", element.className)}
        data-testid={`chart-${element.id}`}
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid
              horizontal={showGrid.horizontal !== false}
              vertical={showGrid.vertical === true}
              strokeDasharray={gridDash}
              stroke="hsl(var(--border, 214.3 31.8% 91.4%))"
            />
            {!opts.xAxis?.hide && (
              <XAxis
                dataKey={xKey}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                dy={8}
              />
            )}
            {!opts.yAxis?.hide && (
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                tickFormatter={yTickFormatter}
                width={48}
              />
            )}
            {showTooltip && (
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--background))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                  fontSize: "13px",
                }}
              />
            )}
            {showLegend && <Legend />}
            {seriesDefs.map((s, i) => (
              <Line
                key={s.key}
                type={curved ? "monotone" : "linear"}
                dataKey={s.key}
                name={s.name ?? s.key}
                stroke={s.color ?? CHART_COLORS[i]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, fill: "white" }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    )
  }

  // ─── Pie / Donut Chart ────────────────────────────────────────
  if (chartType === "pie" || chartType === "donut") {
    const innerR = chartType === "donut" ? (opts.innerRadius ?? 60) : 0
    const outerR = opts.outerRadius ?? 80
    const pieDataKey = yKeys[0] ?? "value"

    return (
      <div
        className={cn("h-[300px] w-full", element.className)}
        data-testid={`chart-${element.id}`}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey={pieDataKey}
              nameKey={xKey}
              cx="50%"
              cy="50%"
              innerRadius={innerR}
              outerRadius={outerR}
              strokeWidth={2}
              stroke="white"
            >
              {data.map((_, i) => (
                <Cell
                  key={`cell-${i}`}
                  fill={
                    seriesDefs[0]?.color
                      ? CHART_COLORS[i % CHART_COLORS.length]
                      : CHART_COLORS[i % CHART_COLORS.length]
                  }
                />
              ))}
            </Pie>
            {showTooltip && <Tooltip />}
            {showLegend && <Legend />}
          </PieChart>
        </ResponsiveContainer>
      </div>
    )
  }

  // ─── Bar Chart (default) ──────────────────────────────────────
  return (
    <div
      className={cn("h-[300px] w-full", element.className)}
      data-testid={`chart-${element.id}`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid
            horizontal={showGrid.horizontal !== false}
            vertical={showGrid.vertical === true}
            strokeDasharray={gridDash}
            stroke="hsl(var(--border, 214.3 31.8% 91.4%))"
          />
          {!opts.xAxis?.hide && (
            <XAxis
              dataKey={xKey}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
              dy={8}
            />
          )}
          {!opts.yAxis?.hide && (
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
              tickFormatter={yTickFormatter}
              width={48}
            />
          )}
          {showTooltip && (
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--background))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
                fontSize: "13px",
              }}
              cursor={{ fill: "hsl(var(--muted, 210 40% 96.1%))", opacity: 0.5 }}
            />
          )}
          {showLegend && <Legend />}
          {seriesDefs.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.name ?? s.key}
              fill={s.color ?? CHART_COLORS[i]}
              radius={[4, 4, 0, 0]}
              stackId={stacked ? "stack" : undefined}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
